/**
 * The commit transition (D3) — the single place ephemeral stream state becomes durable
 * server state.
 *
 * Refetching an entire story after every action is what you do when there is no way to say "just
 * this part". The `chapter` event carries everything the feed needs (P1: chapter, choices,
 * and the post-`settle` character snapshot + `world.time`), so the turn that just cost ~50
 * seconds does not then wait on a round trip to show its own result.
 *
 * What is written, and what is deliberately only *invalidated*:
 *
 * | Cache | Treatment |
 * |---|---|
 * | chapters (infinite) | the new chapter is prepended to the newest page — it is complete except for `summary`, which the summarization job writes later (U2) |
 * | story detail | patched: `chapter_count`, `choices`, death, `updated_at`, and `active_turn` back to null |
 * | story state | character and `world.time` patched from the snapshot; the rest of `world` marked stale **without a refetch** — locations, NPCs and factions are modal-only content and nobody is looking at them |
 * | stories list | invalidated: `chapter_count` and `updated_at` moved, and the list may not even be mounted |
 *
 * Every patch is conditional on the cache already holding the query. Writing into a cache
 * nobody has read yet would seed a partial object that the next mount trusts.
 */
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Chapter, ChapterPage, StoryDetail, StoryState } from '@/api/generated';
import type { ChapterEvent } from '@/api/events';
import { queryKeys } from '@/api/queries';

export async function commitTurn(
  client: QueryClient,
  storyId: string,
  chapter: ChapterEvent,
): Promise<void> {
  writeChapter(client, storyId, chapter);
  patchStory(client, storyId, chapter);
  patchState(client, storyId, chapter);

  await Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.stories() }),
    // Stale, not refetched: the world panel refetches when it opens, and a turn is the one
    // moment this client is certain nothing is waiting on that data.
    client.invalidateQueries({ queryKey: queryKeys.storyState(storyId), refetchType: 'none' }),
  ]);
}

/** The `chapter` event minus the snapshot is exactly a `Chapter` row, but for `summary`. */
export function chapterFromEvent(event: ChapterEvent): Chapter {
  return {
    number: event.chapter_number,
    action: event.action,
    outcome: event.outcome,
    situation: event.situation,
    narration: event.narration,
    choices: event.choices,
    changes: event.changes,
    // U6. Carried through like the dateline below: the event holds the spans the server
    // wrote onto the row, so the cached chapter and a refetched one agree rather than
    // differing by a field the client had to invent.
    mentions: event.mentions,
    // U2's journal line is written by a background job after the turn commits. The newest
    // chapter never has one, and the story feed renders the gap rather than blocking on it.
    summary: null,
    is_death: event.is_death,
    cause_of_death: event.cause_of_death,
    created_at: event.created_at,
    // The dateline. The event does not carry these under their row names, but it carries
    // exactly the same two values — the post-`settle()` clock and where the character ended
    // up — which is what the server denormalized onto the row in the same transaction. So
    // the chapter this writes into the cache and the one a refetch would return agree.
    world_time: event.world.time,
    location: event.character.location,
    // Same rule as the dateline above: the event carries the value the server wrote to the
    // row, so the cached chapter and a refetched one agree rather than differing by a field
    // the client had to invent.
    duration_seconds: event.duration_seconds,
  };
}

function writeChapter(client: QueryClient, storyId: string, event: ChapterEvent): void {
  const key = queryKeys.chapters(storyId);
  if (client.getQueryData(key) === undefined) return;

  client.setQueryData<InfiniteData<ChapterPage, number | null>>(key, (previous) => {
    if (!previous) return previous;
    const [newest, ...older] = previous.pages;
    if (!newest) return previous;

    const row = chapterFromEvent(event);
    // A replayed idempotency key (P2) delivers a chapter the cache may already hold. Replace
    // rather than prepend: two copies of chapter 12 is worse than a stale one.
    const existing = newest.chapters.findIndex((chapter) => chapter.number === row.number);
    const chapters =
      existing === -1
        ? [row, ...newest.chapters]
        : newest.chapters.map((chapter, index) => (index === existing ? row : chapter));

    return { ...previous, pages: [{ ...newest, chapters }, ...older] };
  });
}

function patchStory(client: QueryClient, storyId: string, event: ChapterEvent): void {
  client.setQueryData<StoryDetail>(queryKeys.story(storyId), (previous) => {
    if (!previous) return previous;
    return {
      ...previous,
      chapter_count: event.chapter_number,
      choices: event.choices,
      status: event.is_death ? 'dead' : previous.status,
      cause_of_death: event.cause_of_death ?? previous.cause_of_death,
      updated_at: event.created_at,
      // Ours just finished, and the server holds one lock per story.
      active_turn: null,
    };
  });
}

function patchState(client: QueryClient, storyId: string, event: ChapterEvent): void {
  client.setQueryData<StoryState>(queryKeys.storyState(storyId), (previous) => {
    if (!previous) return previous;
    return {
      ...previous,
      // `StoryState.chapter_number` is the chapter the *next* turn will write.
      chapter_number: event.chapter_number + 1,
      character: event.character,
      world: { ...previous.world, time: event.world.time },
    };
  });
}
